import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import useLogout from "../../hooks/useLogout";
import useAuth from "../../hooks/useAuth";
import { ConfirmationModal } from "../Generic";
import { Icon } from "../../components/Icon";
import logo from "../../assets/icons/W2T_LOGO_PNG_reduced.png";

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MobileNav = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const { pathname } = useLocation();
  const logout = useLogout();
  const { auth } = useAuth();
  const [getLabel] = useLabels();
  const drawerRef = useRef(null);
  const toggleRef = useRef(null);

  // Chiude il drawer a ogni cambio pagina
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Drawer chiuso = non raggiungibile da tastiera e screen reader
  // (React 18 non gestisce la prop `inert`, quindi la impostiamo sul DOM)
  useEffect(() => {
    drawerRef.current?.toggleAttribute("inert", !isOpen);
  }, [isOpen]);

  // Esc, focus trap, blocco dello scroll e ritorno del focus
  useEffect(() => {
    if (!isOpen) return undefined;
    const drawer = drawerRef.current;
    const toggle = toggleRef.current;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        return;
      }
      if (e.key !== "Tab" || !drawer) return;
      const items = [toggle, ...drawer.querySelectorAll(FOCUSABLE)].filter(Boolean);
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    document.body.classList.add("no_scroll");
    drawer?.querySelector(FOCUSABLE)?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("no_scroll");
      toggle?.focus();
    };
  }, [isOpen]);

  return (
    <>
      <header className="mobile_topbar">
        <button
          ref={toggleRef}
          type="button"
          className="mobile_topbar_toggle"
          aria-label={getLabel(isOpen ? "mobileMenuClose" : "mobileMenuOpen")}
          aria-expanded={isOpen}
          aria-controls="mobile_drawer"
          onClick={() => setIsOpen((v) => !v)}
        >
          <Icon icon={isOpen ? "fa-xmark" : "fa-bars"} size="xl" />
        </button>
        <img className="mobile_topbar_logo" src={logo} alt="Wine2Trace" height={32} />
      </header>

      <div
        className={`mobile_drawer_backdrop ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <nav
        id="mobile_drawer"
        ref={drawerRef}
        className={`mobile_drawer ${isOpen ? "open" : ""}`}
        aria-label={getLabel("mobileMenuNavigation")}
      >
        <div className="mobile_drawer_body">{children}</div>
        <div className="mobile_drawer_footer">
          <NavLink className="mobile_drawer_link" to={auth?.user_id ? `/users/${auth.user_id}` : "/"}>
            {getLabel("mobileMenuProfile")}
          </NavLink>
          <button type="button" className="mobile_drawer_link" onClick={() => setIsLogoutOpen(true)}>
            {getLabel("mobileMenuLogout")}
          </button>
        </div>
      </nav>

      <ConfirmationModal
        isOpen={isLogoutOpen}
        onConfirm={logout}
        onClose={() => setIsLogoutOpen(false)}
        description={getLabel("modalLogout")}
      />
    </>
  );
};

export default MobileNav;
